import { getFirestorePathDepartmentId } from '../utils/normalizeIds';

const HEAD_WARD_ROLE = 'HEAD_WARD_NURSE';

export type DepartmentRoleEntry = { id: string; role: string };

export type ReconciledDepartmentAssignment = {
  departmentIds: string[];
  departmentRoleById: Record<string, string>;
  departmentRoles: DepartmentRoleEntry[];
  headWardNurseOf: string[];
  removedDepartmentRoleByIdKeys: string[];
};

const isHeadWardRole = (role: string): boolean => {
  const token = role.trim().toUpperCase().replace(/-/g, '_');
  return token === 'HEAD_WARD_NURSE' || token === 'HWN';
};

const readDepartmentId = (value: unknown): string => getFirestorePathDepartmentId(value);

const readIdList = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const entry of value) {
    const id = readDepartmentId(entry);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
};

const readRoleById = (value: unknown): Record<string, string> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const out: Record<string, string> = {};
  for (const [deptId, role] of Object.entries(value as Record<string, unknown>)) {
    const id = readDepartmentId(deptId);
    if (!id || typeof role !== 'string' || !role.trim()) continue;
    out[id] = role.trim();
  }
  return out;
};

const readDepartmentRoles = (value: unknown): DepartmentRoleEntry[] => {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const out: DepartmentRoleEntry[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== 'object') continue;
    const cast = entry as { id?: unknown; role?: unknown };
    const id = readDepartmentId(cast.id);
    if (!id || seen.has(id)) continue;
    const role = typeof cast.role === 'string' ? cast.role.trim() : '';
    if (!role) continue;
    seen.add(id);
    out.push({ id, role });
  }
  return out;
};

const sameId = (left: string, right: string): boolean => left.toUpperCase() === right.toUpperCase();

/**
 * A ward move must replace the previous ward. Firestore map merges keep old
 * `departmentRoleById` keys, and login used to union every department list.
 * When `departmentRoles` is present it is the current assignment (array writes
 * replace the whole list). Otherwise `departmentRoleById` wins. Older lists are
 * not added back. `departmentsHeadedByUser` fills in only when the user has no
 * explicit assignment.
 */
export function reconcileDepartmentAssignment(input: {
  departmentRoles?: unknown;
  departmentRoleById?: unknown;
  departmentIds?: unknown;
  headWardNurseOf?: unknown;
  departmentsHeadedByUser?: string[];
  authoritativeDepartmentIds?: string[] | null;
  defaultRole?: string;
}): ReconciledDepartmentAssignment {
  const previousRoleById = readRoleById(input.departmentRoleById);
  const explicitRoles = readDepartmentRoles(input.departmentRoles);
  const explicitIds = readIdList(input.departmentIds);
  const previousHeadWard = readIdList(input.headWardNurseOf);
  const headedByUser = readIdList(input.departmentsHeadedByUser);
  const defaultRole = input.defaultRole?.trim() || HEAD_WARD_ROLE;

  const roleFor = (id: string, preferred?: string): string => {
    if (preferred?.trim()) return preferred.trim();
    const fromMap = Object.entries(previousRoleById).find(([key]) => sameId(key, id))?.[1];
    if (fromMap) return fromMap;
    const fromRoles = explicitRoles.find((entry) => sameId(entry.id, id))?.role;
    if (fromRoles) return fromRoles;
    return defaultRole;
  };

  let nextRoles: DepartmentRoleEntry[];
  if (input.authoritativeDepartmentIds != null) {
    nextRoles = readIdList(input.authoritativeDepartmentIds).map((id) => ({
      id,
      role: roleFor(id),
    }));
  } else if (explicitRoles.length > 0) {
    nextRoles = explicitRoles.map((entry) => ({ id: entry.id, role: roleFor(entry.id, entry.role) }));
  } else if (Object.keys(previousRoleById).length > 0) {
    nextRoles = Object.entries(previousRoleById).map(([id, role]) => ({ id, role }));
  } else if (explicitIds.length > 0) {
    nextRoles = explicitIds.map((id) => ({ id, role: roleFor(id) }));
  } else if (previousHeadWard.length > 0) {
    nextRoles = previousHeadWard.map((id) => ({ id, role: roleFor(id, HEAD_WARD_ROLE) }));
  } else {
    nextRoles = headedByUser.map((id) => ({ id, role: roleFor(id, HEAD_WARD_ROLE) }));
  }

  const departmentRoleById: Record<string, string> = {};
  for (const entry of nextRoles) departmentRoleById[entry.id] = entry.role;

  const removedDepartmentRoleByIdKeys = Object.keys(previousRoleById).filter(
    (key) => !Object.prototype.hasOwnProperty.call(departmentRoleById, key),
  );

  return {
    departmentIds: nextRoles.map((entry) => entry.id),
    departmentRoleById,
    departmentRoles: nextRoles,
    headWardNurseOf: nextRoles.filter((entry) => isHeadWardRole(entry.role)).map((entry) => entry.id),
    removedDepartmentRoleByIdKeys,
  };
}
